import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'providers/AuthProvider';
import { useReleaseNotes } from 'hooks/useReleaseNotes';
import { isElectron } from 'utils/environment';
import type { ReleaseNote } from 'types/releaseNotes';

const CURRENT_VERSION_KEY = 'currentVersion';
const LAST_SEEN_VERSION_KEY = 'lastSeenVersion';

export function usePostUpdateReleaseNotes() {
  const isDev = typeof window !== 'undefined' && window.electronAPI?.isDev;
  const { isAuthenticated } = useAuth();

  const isElectronApp = isElectron && !isDev;
  const dialogEnabled = isElectronApp && isAuthenticated;

  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  const [lastSeenVersion, setLastSeenVersion] = useState<string | null | undefined>(undefined);
  const [isOpen, setIsOpen] = useState(false);
  const [releaseNoteToShow, setReleaseNoteToShow] = useState<ReleaseNote | null>(null);
  const { releaseNotes, isLoading } = useReleaseNotes({ enabled: dialogEnabled });

  useEffect(() => {
    if (!isElectronApp) return;

    window.electronAPI.update
      .getCurrentVersion()
      .then(({ version }) => {
        setCurrentVersion(version);
        return window.electronAPI.versions.set(CURRENT_VERSION_KEY, version);
      })
      .catch((err) => {
        console.error('[post-update] Failed to persist current version:', err);
      });
  }, [isElectronApp]);

  useEffect(() => {
    if (!dialogEnabled) return;

    window.electronAPI.versions
      .get(LAST_SEEN_VERSION_KEY)
      .then((value) => {
        setLastSeenVersion(value);
      })
      .catch((err) => {
        console.error('[post-update] Failed to read last seen version:', err);
        setLastSeenVersion(null);
      });
  }, [dialogEnabled]);

  useEffect(() => {
    if (!dialogEnabled || isLoading || !currentVersion || lastSeenVersion === undefined) return;

    if (currentVersion === lastSeenVersion) return;

    const note = releaseNotes.find((rn) => rn.version === currentVersion) ?? null;

    if (note) {
      setLastSeenVersion(currentVersion);
      setReleaseNoteToShow(note);
      setIsOpen(true);
    } else {
      void window.electronAPI.versions
        .set(LAST_SEEN_VERSION_KEY, currentVersion)
        .then(() => setLastSeenVersion(currentVersion))
        .catch((err) =>
          console.error(
            '[post-update] Failed to retrieve (not found) last seen release notes:',
            err
          )
        );
    }
  }, [dialogEnabled, isLoading, currentVersion, lastSeenVersion, releaseNotes]);

  const acknowledgeUpdate = useCallback(() => {
    if (currentVersion) {
      void window.electronAPI.versions
        .set(LAST_SEEN_VERSION_KEY, currentVersion)
        .catch((err) => console.error('[post-update] Failed to acknowledge update:', err));
    }
    setIsOpen(false);
  }, [currentVersion]);

  return { isOpen, releaseNoteToShow, acknowledgeUpdate };
}
